import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { format, subDays, startOfMonth } from "date-fns";
import {
    DollarSign,
    TrendingUp,
    PieChart as PieIcon,
    Plus,
    Trash2,
    CreditCard,
    ShoppingBag,
    Package
} from "lucide-react";
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    BarChart,
    Bar
} from "recharts";
import { showToast } from '../../utils/toastUtils';

const BASE_URL = process.env.REACT_APP_API_URL || "";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d", "#ffc658"];

const StatCard = ({ title, value, icon, subtext, type = "neutral" }) => {
    const styles = {
        neutral: "bg-white text-gray-600 border-gray-100",
        success: "bg-emerald-50 text-emerald-700 border-emerald-100",
        danger: "bg-red-50 text-red-700 border-red-100",
        warning: "bg-amber-50 text-amber-700 border-amber-100",
        primary: "bg-blue-50 text-blue-700 border-blue-100",
    }[type];

    return (
        <div className={`rounded-xl p-6 border shadow-sm transition-all hover:shadow-md bg-white`}>
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
                    <h3 className={`text-2xl font-bold ${type === 'success' ? 'text-emerald-600' : type === 'danger' ? 'text-red-600' : 'text-gray-900'}`}>
                        {value}
                    </h3>
                    {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
                </div>
                <div className={`p-3 rounded-lg ${styles}`}>
                    {React.cloneElement(icon, { size: 24 })}
                </div>
            </div>
        </div>
    );
};

const FinanceDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [dateRange, setDateRange] = useState("30"); // 30 days
    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);

    // Expense Form State
    const [newExpense, setNewExpense] = useState({
        title: "",
        amount: "",
        category: "Operations",
        date: format(new Date(), "yyyy-MM-dd"),
        description: ""
    });

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const end = new Date();
            let start = subDays(end, parseInt(dateRange));
            if (dateRange === "thisMonth") {
                start = startOfMonth(new Date());
            } else if (dateRange === "allTime") {
                start = new Date(0); // 1970-01-01
            }

            // Fetch Analysis
            const analysisRes = await axios.get(`${BASE_URL}/api/expenses/analysis`, {
                params: { startDate: start.toISOString(), endDate: end.toISOString() }
            });
            setData(analysisRes.data);

            // Fetch Recent Expenses
            const expensesRes = await axios.get(`${BASE_URL}/api/expenses`);
            setExpenses(expensesRes.data);

        } catch (err) {
            console.error(err);
            showToast("Error", "Failed to load financial data", "error");
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleAddExpense = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${BASE_URL}/api/expenses`, {
                ...newExpense,
                amount: Number(newExpense.amount)
            });
            showToast("Success", "Expense added", "success");
            setIsExpenseModalOpen(false);
            setNewExpense({ title: "", amount: "", category: "Operations", date: format(new Date(), "yyyy-MM-dd"), description: "" });
            fetchData(); // Refresh data
        } catch (err) {
            showToast("Error", "Failed to add expense", "error");
        }
    };

    const handleDeleteExpense = async (id) => {
        if (!window.confirm("Delete this expense?")) return;
        try {
            await axios.delete(`${BASE_URL}/api/expenses/${id}`);
            showToast("Success", "Expense deleted", "success");
            fetchData();
        } catch (err) {
            showToast("Error", "Failed to delete expense", "error");
        }
    };

    // Prepare Chart Data
    const expenseBreakdown = useMemo(() => {
        if (!expenses.length) return [];
        // Aggregate by category from *fetched* expenses (note: fetching all expenses might be heavy, ideally filter by date too but for now simplicity)
        // Actually the `data` object has totalExpenses but not breakdown. Let's use `expenses` list which is 'all recent'.
        // We should better filter expenses by the selected date range for the chart?
        // For now, let's just show breakdown of *all* recent expenses or filter client side.
        // Let's filter client side based on date range for consistency.

        const end = new Date();
        let start = subDays(end, parseInt(dateRange));
        if (dateRange === "thisMonth") start = startOfMonth(new Date());
        if (dateRange === "allTime") start = new Date(0);

        const filtered = expenses.filter(e => new Date(e.date) >= start && new Date(e.date) <= end);

        const grouped = filtered.reduce((acc, curr) => {
            acc[curr.category] = (acc[curr.category] || 0) + curr.amount;
            return acc;
        }, {});

        return Object.keys(grouped).map(k => ({ name: k, value: grouped[k] }));
    }, [expenses, dateRange]);


    if (loading && !data) return <div className="p-8 flex justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div></div>;

    return (
        <div className="min-h-screen bg-gray-50 p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <DollarSign className="text-emerald-600" /> Profit & Expense Intelligence
                    </h1>
                    <p className="text-sm text-gray-500">Real-time financial health monitoring.</p>
                </div>

                <div className="flex gap-3">
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="7">Last 7 Days</option>
                        <option value="30">Last 30 Days</option>
                        <option value="thisMonth">This Month</option>
                        <option value="90">Last 3 Months</option>
                        <option value="allTime">All Time</option>
                    </select>

                    <button
                        onClick={() => setIsExpenseModalOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm transition-all"
                    >
                        <Plus size={16} /> Log Expense
                    </button>
                </div>
            </div>

            {/* KPI Section */}
            {data && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard
                        title="Total Revenue"
                        value={`Rs. ${data.totalRevenue.toLocaleString()}`}
                        icon={<ShoppingBag />}
                        type="primary"
                        subtext={`${data.orderCount} Orders`}
                    />
                    <StatCard
                        title="Cost of Goods (COGS)"
                        value={`Rs. ${data.totalCOGS.toLocaleString()}`}
                        icon={<Package />}
                        type="warning"
                        subtext="Product base costs"
                    />
                    <StatCard
                        title="Operational Expenses"
                        value={`Rs. ${data.totalExpenses.toLocaleString()}`}
                        icon={<CreditCard />}
                        type="danger"
                        subtext={`${data.expenseCount} entries recorded`}
                    />
                    <StatCard
                        title="Net Profit"
                        value={`Rs. ${data.netProfit.toLocaleString()}`}
                        icon={<TrendingUp />}
                        type={data.netProfit >= 0 ? "success" : "danger"}
                        subtext="Revenue - COGS - Expenses"
                    />
                </div>
            )}

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Waterfall / Composition Chart (Simulated with Bar for now or Pie) */}
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6">Financial Composition</h3>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={[
                                { name: "Revenue", value: data?.totalRevenue, fill: "#3B82F6" },
                                { name: "COGS", value: data?.totalCOGS, fill: "#F59E0B" },
                                { name: "Expenses", value: data?.totalExpenses, fill: "#EF4444" },
                                { name: "Net Profit", value: data?.netProfit, fill: data?.netProfit >= 0 ? "#10B981" : "#EF4444" }
                            ]}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                                <YAxis axisLine={false} tickLine={false} tickFormatter={(val) => `Rs.${val / 1000}k`} />
                                <Tooltip formatter={(val) => `Rs. ${val.toLocaleString()}`} cursor={{ fill: 'transparent' }} />
                                <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={60}>
                                    {
                                        [
                                            { fill: "#3B82F6" },
                                            { fill: "#F59E0B" },
                                            { fill: "#EF4444" },
                                            { fill: data?.netProfit >= 0 ? "#10B981" : "#EF4444" }
                                        ].map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))
                                    }
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Expense Breakdown */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Expense Breakdown</h3>
                    <p className="text-xs text-gray-500 mb-6">Where is the money going?</p>
                    <div className="h-[260px] w-full">
                        {expenseBreakdown.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={expenseBreakdown}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {expenseBreakdown.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(val) => `Rs. ${val.toLocaleString()}`} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                <PieIcon size={48} className="mb-2 opacity-50" />
                                <p>No expenses recorded</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Recent Expenses Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">Recent Expense Log</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 font-medium">
                            <tr>
                                <th className="px-6 py-3">Date</th>
                                <th className="px-6 py-3">Title</th>
                                <th className="px-6 py-3">Category</th>
                                <th className="px-6 py-3 text-right">Amount</th>
                                <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {expenses.length === 0 ? (
                                <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">No expenses found. Add one to see it here.</td></tr>
                            ) : (
                                expenses.slice(0, 10).map((expense) => (
                                    <tr key={expense._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-3 text-gray-600">{format(new Date(expense.date), "MMM dd, yyyy")}</td>
                                        <td className="px-6 py-3 font-medium text-gray-900">{expense.title}</td>
                                        <td className="px-6 py-3">
                                            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">{expense.category}</span>
                                        </td>
                                        <td className="px-6 py-3 text-right font-bold text-gray-700">Rs. {expense.amount.toLocaleString()}</td>
                                        <td className="px-6 py-3 text-right">
                                            <button onClick={() => handleDeleteExpense(expense._id)} className="text-red-400 hover:text-red-600 p-1">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Expense Modal */}
            {isExpenseModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Log New Expense</h2>
                        <form onSubmit={handleAddExpense} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Expense Title</label>
                                <input required type="text" placeholder="e.g., Office Rent" className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500"
                                    value={newExpense.title} onChange={e => setNewExpense({ ...newExpense, title: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Rs)</label>
                                    <input required type="number" min="0" className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500"
                                        value={newExpense.amount} onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                    <input required type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500"
                                        value={newExpense.date} onChange={e => setNewExpense({ ...newExpense, date: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <select className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500"
                                    value={newExpense.category} onChange={e => setNewExpense({ ...newExpense, category: e.target.value })}
                                >
                                    <option value="Operations">Operations</option>
                                    <option value="Marketing">Marketing</option>
                                    <option value="Salary">Salary</option>
                                    <option value="Rent">Rent</option>
                                    <option value="Packaging">Packaging</option>
                                    <option value="Shipping">Shipping</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                                <textarea rows="2" className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500"
                                    value={newExpense.description} onChange={e => setNewExpense({ ...newExpense, description: e.target.value })}
                                ></textarea>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button type="button" onClick={() => setIsExpenseModalOpen(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200">Cancel</button>
                                <button type="submit" className="flex-1 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 shadow-lg shadow-blue-200">Save Expense</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinanceDashboard;
